/**
 * prism-fanuc-karel
 *
 * Prism.js language definition for the FANUC Karel (.kl) programming language.
 *
 * Usage (ES module / Vite / webpack):
 *
 *   import Prism from 'prismjs';
 *   import registerKarel from 'prism-fanuc-karel';
 *   registerKarel(Prism);
 *
 * Usage (browser CDN / script tag – after prism.js has been loaded):
 *
 *   <script src="prism.js"></script>
 *   <script src="node_modules/prism-fanuc-karel/index.js"></script>
 *   <!-- the self-invoking tail registers the language automatically -->
 *
 * Then mark code blocks with:
 *   <code class="language-fanuc-karel"> … </code>
 *
 * The package is peer-dependency-free; it never imports prismjs itself.
 * Call  registerKarel(Prism)  to attach the grammar to whatever Prism
 * instance your project already uses.
 */

// ---------------------------------------------------------------------------
// Helpers (kept as plain strings for readability / easy editing)
// ---------------------------------------------------------------------------

var TYPES =
    'ARRAY|BOOLEAN|BYTE|CAM_SETUP|CONFIG|DISP_DAT_T|FILE|INTEGER' +
    '|JOINTPOS(?:[1-9])?|MODEL|PATH|POSITION|QUEUE_TYPE|REAL|SHORT' +
    '|STD_PTH_NODE|STRING|VECTOR|VIS_PROCESS|XYZWPR|XYZWPREXT' +
    '|STRUCTURE|ENDSTRUCTURE';

var ACTIONS =
    'NOABORT|NOMESSAGE|NOPAUSE|PAUSE|PULSE|RESUME|STOP|UNHOLD|UNPAUSE';

var FUNCTIONS =
    'ABS|ACOS|APPROACH|ARRAY_LEN|ASIN|ATAN2|ATTACH|BYNAME|BYTES_LEFT' +
    '|CHR|COS|CURJPOS|CURPOS|CURR_PROG|EXP|FRAME|GET_FILE_POS' +
    '|GET_JPOS_REG|GET_JPOS_TPE|GET_PORT_ATR|GET_POS_REG|GET_POS_TPE' +
    '|GET_USEC_TIM|INDEX|IN_RANGE|INV|IO_STATUS|J_IN_RANGE|JOINT2POS' +
    '|LN|MIRROR|MOTION_CTL|NODE_SIZE|ORD|ORIENT|PATH_LEN|POS|POS2JOINT' +
    '|ROUND|SEMA_COUNT|SIN|SQRT|STR_LEN|SUB_STR|TAN|TRUNC|UNINIT';

var PROCEDURES =
    'ABORT_TASK|ACT_SCREEN|ACT_TBL|ADD_BYNAMEPC|ADD_DICT|ADD_INTPC' +
    '|ADD_REALPC|ADD_STRINGPC|APPEND_NODE|APPEND_QUEUE|ATT_WINDOW_D' +
    '|ATT_WINDOW_S|AVL_POS_NUM|BYTES_AHEAD|CALL_PROG|CALL_PROGLIN' +
    '|CHECK_DICT|CHECK_EPOS|CHECK_NAME|CLEAR|CLEAR_SEMA|CLOSE_TEP' +
    '|CLR_IO_STAT|CLR_PORT_SIM|CLR_POS_REG|CNC_DYN_DISB|CNC_DYN_DISE' +
    '|CNC_DYN_DISI|CNC_DYN_DISP|CNC_DYN_DISR|CNC_DYN_DISS|CNCL_STP_MTN' +
    '|CNV_CNF_STRG|CNV_CONF_STR|CNV_INT_STR|CNV_JPOS_REL|CNV_REAL_STR' +
    '|CNV_REL_JPOS|CNV_STR_CONF|CNV_STR_INT|CNV_STR_REAL|CNV_STR_TIME' +
    '|CNV_TIME_STR|COMPARE_FILE|CONT_TASK|COPY_FILE|COPY_PATH|COPY_QUEUE' +
    '|COPY_TPE|CREATE_TPE|CREATE_VAR|DAQ_CHECKP|DAQ_REGPIPE|DAQ_START' +
    '|DAQ_STOP|DAQ_UNREG|DAQ_WRITE|DEF_SCREEN|DEF_WINDOW|DELETE_FILE' +
    '|DELETE_NODE|DELETE_QUEUE|DEL_INST_TPE|DET_WINDOW|DISCTRL_ALPH' +
    '|DISCTRL_FORM|DISCTRL_LIST|DISCTRL_PLMN|DISCTRL_SBMN|DISCTRL_TBL' +
    '|DISMOUNT_DEV|DOSFILE_INF|ERR_DATA|FILE_LIST|FORCE_SPMENU|FORMAT_DEV' +
    '|GET_ATTR_PRG|GET_PORT_ASG|GET_PORT_CMT|GET_PORT_MOD|GET_PORT_SIM' +
    '|GET_PORT_VAL|GET_POS_FRM|GET_POS_TYP|GET_PREG_CMT|GET_QUEUE' +
    '|GET_REG|GET_REG_CMT|GET_SREG_CMT|GET_STR_REG|GET_TIME|GET_TPE_CMT' +
    '|GET_TPE_PRM|GET_TSK_INFO|GET_USEC_SUB|GET_VAR|INI_DYN_DISB' +
    '|INI_DYN_DISE|INI_DYN_DISI|INI_DYN_DISP|INI_DYN_DISR|INI_DYN_DISS' +
    '|INIT_QUEUE|INIT_TBL|INSERT_NODE|INSERT_QUEUE|IO_MOD_TYPE|KCL' +
    '|KCL_NO_WAIT|KCL_STATUS|LOAD|LOAD_STATUS|LOCK_GROUP|MODIFY_QUEUE' +
    '|MOUNT_DEV|MOVE_FILE|MSG_CONNECT|MSG_DISCO|MSG_PING|OPEN_TPE' +
    '|PAUSE_TASK|PEND_SEMA|PIPE_CONFIG|POP_KEY_RD|POS_REG_TYPE|POST_ERR' +
    '|POST_ERR_L|POST_SEMA|PRINT_FILE|PROG_BACKUP|PROG_CLEAR|PROG_RESTORE' +
    '|PROG_LIST|PURGE_DEV|PUSH_KEY_RD|READ_DICT|READ_DICT_V|READ_KB' +
    '|REMOVE_DICT|RENAME_FILE|RENAME_VAR|RENAME_VARS|RESET|RUN_TASK|SAVE' +
    '|SAVE_DRAM|SELECT_TPE|SEND_DATAPC|SEND_EVENTPC|SET_ATTR_PRG' +
    '|SET_CURSOR|SET_EPOS_REG|SET_EPOS_TPE|SET_FILE_ATR|SET_FILE_POS' +
    '|SET_INT_REG|SET_JPOS_REG|SET_JPOS_TPE|SET_LANG|SET_PERCH' +
    '|SET_PORT_ASG|SET_PORT_ATR|SET_PORT_CMT|SET_PORT_MOD|SET_PORT_SIM' +
    '|SET_PORT_VAL|SET_POS_REG|SET_POS_TPE|SET_PREG_CMT|SET_REAL_REG' +
    '|SET_REG|SET_SREG_CMT|SET_STR_REG|SET_TIME|SET_TPE_CMT|SET_TRNS_TPE' +
    '|SET_TSK_ATTR|SET_TSK_NAME|SET_VAR|TRANSLATE|UNLOCK_GROUP|UNPOS' +
    '|V_CAM_CALIB|V_GET_OFFSET|V_GET_PASSFL|V_GET_QUEUE|V_INIT_QUEUE' +
    '|V_RALC_QUEUE|V_RUN_FIND|V_SET_REF|V_START_VTRK|V_STOP_VTRK' +
    '|VAR_INFO|VAR_LIST|VOL_SPACE|VREG_FND_POS|VREG_OFFSET|WRITE_DICT' +
    '|WRITE_DICT_V|XML_ADDTAG|XML_GETDATA|XML_REMTAG|XML_SCAN|XML_SETVAR' +
    '|CMT';

var CONTROL_KEYWORDS =
    'IF|THEN|ELSE|ENDIF|SELECT|ENDSELECT|CASE|OF' +
    '|WHILE|DO|ENDWHILE|FOR|TO|ENDFOR' +
    '|EVAL|FROM|IN|WHEN|WITH' +
    '|AND|DIV|MOD|NOT|OR';

var STATEMENT_KEYWORDS =
    'ABORT|CONDITION|ENDCONDITION|CONTINUE|DELAY|ERROR|EVENT' +
    '|HOLD|READ|RELEASE|REPEAT|RETURN|SEMAPHORE|UNTIL|USING|ENDUSING' +
    '|WRITE|COMMAND|TPENABLE';

// ---------------------------------------------------------------------------
// Grammar definition factory
// ---------------------------------------------------------------------------

function buildGrammar() {
    return {

        // -- Comments (-- to end of line) ---------------------------------------
        'comment': {
            pattern: /--[^\r\n]*/,
            greedy: true
        },

        // -- String literals ('…', '' is an escaped single-quote) ---------------
        'string': {
            pattern: /'(?:''|[^'\r\n])*'/,
            greedy: true
        },

        // -- Compiler directives (%NOLOCKGROUP, %STACKSIZE=512, …) --------------
        'directive': {
            pattern: /%[A-Za-z][A-Za-z0-9_]*/,
            alias: 'tag'
        },

        // -- Boolean literals ---------------------------------------------------
        'boolean': {
            pattern: /\b(?:FALSE|TRUE)\b/i
        },

        // -- PROGRAM <name> declaration -----------------------------------------
        'program-declaration': {
            pattern: /\bPROGRAM\b[ \t]+[_$A-Za-z][_$A-Za-z0-9]*/i,
            inside: {
                'storage': /\bPROGRAM\b/i,
                'entity': /[_$A-Za-z][_$A-Za-z0-9]*/
            }
        },

        // -- ROUTINE <name> declaration -----------------------------------------
        'routine-declaration': {
            pattern: /\bROUTINE\b[ \t]+[_$A-Za-z][_$A-Za-z0-9]*/i,
            inside: {
                'storage': /\bROUTINE\b/i,
                'entity': /[_$A-Za-z][_$A-Za-z0-9]*/
            }
        },

        // -- END <name> ---------------------------------------------------------
        'end-declaration': {
            pattern: /\bEND\b[ \t]+[_$A-Za-z][_$A-Za-z0-9]*/i,
            inside: {
                'keyword': /\bEND\b/i,
                'entity': /[_$A-Za-z][_$A-Za-z0-9]*/
            }
        },

        // -- BEGIN keyword (block start) ----------------------------------------
        'begin-block': {
            pattern: /\bBEGIN\b/i,
            alias: 'keyword'
        },

        // -- Data types ---------------------------------------------------------
        'type': {
            pattern: new RegExp('\\b(?:' + TYPES + ')\\b', 'i'),
            alias: 'class-name'
        },

        // -- Robot action / state-control keywords (PAUSE, STOP, PULSE, …) -----
        'action': {
            pattern: new RegExp('\\b(?:' + ACTIONS + ')\\b', 'i')
        },

        // -- Built-in functions (return a value) --------------------------------
        'builtin-function': {
            pattern: new RegExp('\\b(?:' + FUNCTIONS + ')\\b', 'i'),
            alias: 'function'
        },

        // -- Built-in procedures (void) -----------------------------------------
        'builtin-procedure': {
            pattern: new RegExp('\\b(?:' + PROCEDURES + ')\\b', 'i'),
            alias: 'procedure'
        },

        // -- Multi-word statement keywords (must precede single-word keywords) --
        'statement-multiword': {
            pattern: /\b(?:CANCEL\s+FILE|CLOSE\s+FILE|CLOSE\s+HAND|CONNECT\s+TIMER|DISABLE\s+CONDITION|DISCONNECT\s+TIMER|ENABLE\s+CONDITION|GO\s+TO|OPEN\s+FILE|OPEN\s+HAND|PURGE\s+CONDITION|RELAX\s+HAND|WAIT\s+FOR)\b/i,
            alias: 'keyword'
        },

        // -- Control-flow and statement keywords --------------------------------
        'keyword': {
            pattern: new RegExp(
                '\\b(?:' + CONTROL_KEYWORDS + '|' + STATEMENT_KEYWORDS + ')\\b',
                'i'
            )
        },

        // -- Declaration-section keywords (CONST, VAR, TYPE, STRUCT) -----------
        'variable-declaration': {
            pattern: /\b(?:CONST|STRUCT|TYPE|VAR)\b/i,
            alias: 'variable'
        },

        // -- Operators ----------------------------------------------------------
        'operator': [
            {
                // Symbol operators only — word operators (AND, OR, NOT, DIV, MOD) are keywords
                pattern: />=<|<>|<=|>=|:=|[+\-*/=<>:#@]/
            }
        ],

        // -- Numeric literals ---------------------------------------------------
        'number': /\b\d+(?:\.\d+)?\b/,

        // -- Punctuation --------------------------------------------------------
        'punctuation': /[()[\],.;]/
    };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register the FANUC Karel language with a Prism instance.
 *
 * @param {object} Prism - The Prism object from `import Prism from 'prismjs'`
 */
function registerKarelLanguage(Prism) {
    var grammar = buildGrammar();
    Prism.languages['fanuc-karel'] = grammar;
    // Convenient aliases
    Prism.languages['karel'] = grammar;
    Prism.languages['kl']    = grammar;
}

// ES module default export
export default registerKarelLanguage;
export { registerKarelLanguage as register };

// Browser global / CDN self-registration
// If Prism is already available as a global (e.g. plain <script> usage),
// register automatically so no extra call is needed.
if (typeof globalThis !== 'undefined' && globalThis.Prism) {
    registerKarelLanguage(globalThis.Prism);
} else if (typeof window !== 'undefined' && window.Prism) {
    registerKarelLanguage(window.Prism);
}

